// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {useCallback, useEffect, useRef, useState} from 'react'

import {getAnalyticsEvent} from '../../lib/utils/analytics'
import {t} from '../../lib/utils/localization'
import {usePrefersReducedMotion} from '../../lib/utils/videos'

export type AutoPlayVideoState = 'playing' | 'paused' | 'ended'

export interface AutoPlayVideoCopy {
  label: {
    play: string
    pause: string
    replay: string
  }
  ariaLabel: {
    play: string
    pause: string
    replay: string
  }
}

export interface AutoPlayVideoAnalyticsSnakeCaseOptions {
  action?: boolean
  tag?: boolean
  context?: boolean
  location?: boolean
}

export interface AutoPlayVideoAnalyticsOptions {
  /** Analytics context, e.g. `video` or `demo_gif`. */
  context: string
  /** Analytics location, e.g. the section the video lives in. */
  location?: string
  /** Analytics tag. Defaults to `button`. */
  tag?: string
  /** Forwarded to swp-core `getAnalyticsEvent` to control per-field snake_casing. */
  snakeCaseOptions?: AutoPlayVideoAnalyticsSnakeCaseOptions
}

export interface UseAutoPlayVideoOptions {
  /** Localized labels and aria labels for the play/pause/replay button. Defaults to translated copy. */
  copy?: AutoPlayVideoCopy
  /**
   * When `true` (default) an IntersectionObserver drives playback: the video
   * starts playing once it scrolls into view. Whether it also pauses when it
   * leaves the viewport depends on `intersectionBehavior` (only `'continuous'`
   * pauses on leave). Disable for videos that should autoplay on mount, such as
   * hero videos that are always in view.
   */
  enableIntersectionObserver?: boolean
  /**
   * Controls how the IntersectionObserver behaves once the video has entered the
   * viewport. `'once'` (default) plays a single time and then stops observing —
   * the recap behavior. `'continuous'` keeps playing on enter and pausing on
   * leave for as long as the video is mounted.
   */
  intersectionBehavior?: 'once' | 'continuous'
  intersectionObserverOptions?: {
    threshold?: number
    rootMargin?: string
  }
  /**
   * Analytics configuration for the play/pause button. When provided, the hook
   * builds the `data-analytics-event` attribute with swp-core's `getAnalyticsEvent`
   * using the current action (`play`, `pause`, or `replay`).
   */
  analytics?: AutoPlayVideoAnalyticsOptions
  /**
   * Escape hatch to fully control the button's analytics attributes. Receives the
   * current action and takes precedence over `analytics`.
   */
  getButtonAnalyticsAttributes?: (action: string) => Record<string, unknown>
}

export interface UseAutoPlayVideoResult {
  /** Callback ref to attach to each `<video>` element the hook should control. */
  registerVideo: (element: HTMLVideoElement | null) => () => void
  videoState: AutoPlayVideoState
  buttonLabel: string
  buttonAriaLabel: string
  /** `true` while the video is paused or has ended (button shows the play icon). */
  buttonPressed: boolean
  /** Toggle handler for the play/pause/replay button. */
  toggle: () => void
  /** Attach to each `<video>`'s `onPlay` to keep the button in sync. */
  onPlay: () => void
  /** Attach to each `<video>`'s `onPause` to keep the button in sync. */
  onPause: () => void
  /** Attach to each `<video>`'s `onEnded` to surface the replay state. */
  onEnded: () => void
  /** Analytics data attributes to spread onto the play/pause button. */
  buttonAnalyticsAttributes: Record<string, unknown>
}

export function getDefaultAutoPlayVideoCopy(): AutoPlayVideoCopy {
  return {
    label: {
      play: t('Play'),
      pause: t('Pause'),
      replay: t('Replay'),
    },
    ariaLabel: {
      play: t('Video is paused. Click to play.'),
      pause: t('Video is playing. Click to pause.'),
      replay: t('Video has ended. Click to replay.'),
    },
  }
}

const ANALYTICS_ACTION_BY_STATE: Record<AutoPlayVideoState, string> = {
  playing: 'pause',
  paused: 'play',
  ended: 'replay',
}

function resolveButtonAnalyticsAttributes(
  action: string,
  analytics?: AutoPlayVideoAnalyticsOptions,
  getButtonAnalyticsAttributes?: (action: string) => Record<string, unknown>,
): Record<string, unknown> {
  if (getButtonAnalyticsAttributes) return getButtonAnalyticsAttributes(action)

  if (analytics) {
    return getAnalyticsEvent(
      {action, tag: analytics.tag ?? 'button', context: analytics.context, location: analytics.location},
      analytics.snakeCaseOptions,
    )
  }

  return {}
}

const DEFAULT_INTERSECTION_OPTIONS = {
  threshold: 0.5, // Play once 50% of the height is...
  rootMargin: '-25% 0% -25% 0%', // ...in the center 50% of the viewport
} as const

function seekToStart(video: HTMLVideoElement) {
  video.currentTime = 0
}

export function useAutoPlayVideo(options: UseAutoPlayVideoOptions = {}): UseAutoPlayVideoResult {
  const {
    copy = getDefaultAutoPlayVideoCopy(),
    enableIntersectionObserver = true,
    intersectionBehavior = 'once',
    intersectionObserverOptions,
    analytics,
    getButtonAnalyticsAttributes,
  } = options

  const videosRef = useRef<Set<HTMLVideoElement>>(new Set())
  const [observedElement, setObservedElement] = useState<HTMLVideoElement | null>(null)
  const isReducedMotion = usePrefersReducedMotion()

  const [videoState, setVideoState] = useState<AutoPlayVideoState>('playing')
  const [buttonLabel, setButtonLabel] = useState(copy.label.pause)
  const [buttonPressed, setButtonPressed] = useState(false)
  const [buttonAriaLabel, setButtonAriaLabel] = useState(copy.ariaLabel.pause)

  const setPausedButtonState = useCallback(() => {
    setVideoState('paused')
    setButtonLabel(copy.label.play)
    setButtonPressed(true)
    setButtonAriaLabel(copy.ariaLabel.play)
  }, [copy.ariaLabel.play, copy.label.play])

  const setPlayingButtonState = useCallback(() => {
    setVideoState('playing')
    setButtonLabel(copy.label.pause)
    setButtonAriaLabel(copy.ariaLabel.pause)
    setButtonPressed(false)
  }, [copy.ariaLabel.pause, copy.label.pause])

  const setReplayButtonState = useCallback(() => {
    setVideoState('ended')
    setButtonLabel(copy.label.replay)
    setButtonAriaLabel(copy.ariaLabel.replay)
    setButtonPressed(true)
  }, [copy.ariaLabel.replay, copy.label.replay])

  const playAll = useCallback(() => {
    for (const video of videosRef.current) {
      const playPromise = video.play()
      // eslint-disable-next-line github/no-then
      if (playPromise !== undefined) playPromise.catch(() => {})
    }
  }, [])

  const pauseAll = useCallback(() => {
    for (const video of videosRef.current) video.pause()
  }, [])

  const seekAllToStart = useCallback(() => {
    for (const video of videosRef.current) seekToStart(video)
  }, [])

  const registerVideo = useCallback((element: HTMLVideoElement | null) => {
    if (element) {
      videosRef.current.add(element)
      setObservedElement(previous => previous ?? element)
    }

    return () => {
      if (!element) return
      videosRef.current.delete(element)
      setObservedElement(previous =>
        previous === element ? (videosRef.current.values().next().value ?? null) : previous,
      )
    }
  }, [])

  useEffect(() => {
    const videos = videosRef.current
    return () => {
      videos.clear()
    }
  }, [])

  const toggle = useCallback(() => {
    if (videosRef.current.size === 0) return

    if (videoState === 'playing') {
      setPausedButtonState()
      pauseAll()
      return
    }

    setPlayingButtonState()
    if (videoState === 'ended') seekAllToStart()
    playAll()
  }, [pauseAll, playAll, seekAllToStart, setPausedButtonState, setPlayingButtonState, videoState])

  useEffect(() => {
    if (videosRef.current.size === 0) return

    if (isReducedMotion) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPausedButtonState()
      pauseAll()
      return
    }

    // When an IntersectionObserver drives playback, let it own the initial
    // play/pause state so off-screen videos don't autoplay (and start
    // buffering) on mount. Videos without an observer autoplay immediately.
    if (!enableIntersectionObserver) {
      setPlayingButtonState()
      playAll()
    }
  }, [enableIntersectionObserver, isReducedMotion, pauseAll, playAll, setPausedButtonState, setPlayingButtonState])

  useEffect(() => {
    if (!enableIntersectionObserver || !observedElement) return

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (isReducedMotion) continue
          if (entry.isIntersecting) {
            setPlayingButtonState()
            playAll()
            if (intersectionBehavior === 'once') observer.unobserve(observedElement)
          } else {
            setPausedButtonState()
            pauseAll()
          }
        }
      },
      {
        threshold: intersectionObserverOptions?.threshold ?? DEFAULT_INTERSECTION_OPTIONS.threshold,
        rootMargin: intersectionObserverOptions?.rootMargin ?? DEFAULT_INTERSECTION_OPTIONS.rootMargin,
      },
    )

    observer.observe(observedElement)

    return () => {
      observer.unobserve(observedElement)
    }
  }, [
    enableIntersectionObserver,
    intersectionBehavior,
    intersectionObserverOptions?.rootMargin,
    intersectionObserverOptions?.threshold,
    isReducedMotion,
    observedElement,
    pauseAll,
    playAll,
    setPausedButtonState,
    setPlayingButtonState,
  ])

  const analyticsAction = ANALYTICS_ACTION_BY_STATE[videoState]
  const buttonAnalyticsAttributes = resolveButtonAnalyticsAttributes(
    analyticsAction,
    analytics,
    getButtonAnalyticsAttributes,
  )

  return {
    registerVideo,
    videoState,
    buttonLabel,
    buttonAriaLabel,
    buttonPressed,
    toggle,
    onPlay: setPlayingButtonState,
    onPause: setPausedButtonState,
    onEnded: setReplayButtonState,
    buttonAnalyticsAttributes,
  }
}

export default useAutoPlayVideo
